import React from "react";
import { FileText, ExternalLink } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useSeoMeta, Breadcrumb } from "../lib/landingShared";

const DOCS = [
  { title: "Informativa Privacy",                            desc: "Trattamento dei dati personali ai sensi del Regolamento UE 2016/679 (GDPR).", href: "/privacy.pdf", key: "privacy" },
  { title: "Modulo Unico Precontrattuale — NO IBIP",          desc: "Allegato 3 MUP 2025 — informativa precontrattuale per prodotti non IBIP.",   href: "/mup-no-ibip.pdf", key: "mup-no-ibip" },
  { title: "Modulo Unico Precontrattuale — IBIP",             desc: "Allegato 4 MUP 2025 — informativa precontrattuale per prodotti d'investimento assicurativo (IBIP).", href: "/altro.pdf", key: "mup-ibip" },
  { title: "Constatazione Amichevole di Incidente",           desc: "Modulo CID da utilizzare in caso di sinistro stradale.", href: "/cai.pdf", key: "cai" },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Compliance e trasparenza — Da Pra Assicurazioni",
  url: "https://dapraassicurazioni.it/compliance",
  description: "Documenti informativi, privacy, MUP NO IBIP, MUP IBIP, CID e informazioni obbligatorie sull'intermediario Da Pra Assicurazioni.",
};

export default function Compliance() {
  useSeoMeta({
    title: "Compliance e Trasparenza | Da Pra Assicurazioni",
    description: "Documenti informativi, MUP NO IBIP, MUP IBIP, CID, privacy policy e informazioni obbligatorie sull'intermediario.",
    keywords: "compliance, MUP, IBIP, CID, privacy, intermediario, RUI, PEC, trasparenza assicurativa",
    canonical: "https://dapraassicurazioni.it/compliance",
    jsonLd: JSON_LD, jsonLdId: "ld-compliance",
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <Breadcrumb current={{ label: "Compliance", href: "/compliance" }} />

      {/* Hero */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#C5A059" }}>Trasparenza · Compliance</div>
            <h1 className="mt-4 font-serif text-4xl md:text-6xl leading-tight text-slate-900">
              Compliance e <span className="italic">trasparenza.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
              Ci muoviamo con etica, ci comportiamo con integrità. Come previsto dalla normativa,
              rendiamo disponibili i documenti e le informazioni obbligatorie sul rapporto assicurativo e sull'intermediario.
            </p>
          </div>
        </div>
      </section>

      {/* Documenti */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 gap-6">
            {DOCS.map((d) => (
              <a
                key={d.key}
                href={d.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`compliance-doc-${d.key}`}
                className="group flex items-start gap-4 border border-slate-200 bg-white p-6 hover:border-slate-900 transition-colors"
              >
                <div className="w-11 h-11 bg-slate-900 text-[#C5A059] flex items-center justify-center flex-shrink-0">
                  <FileText size={20} />
                </div>
                <div className="flex-grow">
                  <div className="overline text-slate-500">Documento</div>
                  <div className="mt-1 font-serif text-lg text-slate-900 leading-snug">{d.title}</div>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d.desc}</p>
                </div>
                <ExternalLink size={16} className="text-slate-400 group-hover:text-slate-900 transition-colors" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Intermediario */}
      <section className="bg-[#FAF9F6] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="overline" style={{ color: "#C5A059" }}>L'intermediario</div>
          <h2 className="mt-3 font-serif text-2xl md:text-3xl text-slate-900">Informazioni obbligatorie</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-x-10 gap-y-6 text-sm text-slate-700 max-w-4xl">
            <div>
              <div className="overline text-slate-500 text-[10px]">Ragione sociale</div>
              <div className="mt-1 font-medium text-slate-900">Da Pra Assicurazioni S.r.l.</div>
            </div>
            <div>
              <div className="overline text-slate-500 text-[10px]">Sede</div>
              <div className="mt-1 font-medium text-slate-900">Milano</div>
            </div>
            <div>
              <div className="overline text-slate-500 text-[10px]">Iscrizione RUI</div>
              <div className="mt-1 font-medium text-slate-900">Sezione A — dati completi disponibili in agenzia</div>
            </div>
            <div>
              <div className="overline text-slate-500 text-[10px]">PEC</div>
              <div className="mt-1 font-medium text-slate-900">daprassicurazioni@pec.it</div>
            </div>
            <div className="md:col-span-2">
              <div className="overline text-slate-500 text-[10px]">Responsabile dell'intermediazione</div>
              <div className="mt-1 text-slate-700">Come previsto dalla normativa IVASS, il responsabile dell'intermediazione è indicato nella documentazione precontrattuale (MUP).</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
